import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpload } from '../contexts/UploadContext';

// Keep-Original opt-in row. Plain factual label + one-line helper. Visual
// treatment: muted card sitting INSIDE an upload surface so it reads as
// "an option for this upload" rather than chrome attached to the page.
// Tabular checkbox + small label + dimmer helper line under it.
//
// Two visibility states, decided by the consumer (UploadZone):
//   1. Not rendered — anonymous viewers. Presence-as-upsell only makes
//      sense once the viewer has an account they could upgrade.
//   2. Rendered with `disabled` — authenticated free/unpaid users. Row
//      is greyed (opacity-70), checkbox is locked, a "Pro" pill sits next
//      to the label, and the whole row routes to /pro on click. The row
//      exists specifically as an upsell surface: absent-from-UI would
//      leave the free user unaware the option exists at all.
//   3. Rendered fully functional — Pro/Beta. Behaves the same as before.
//
// The visibility decision lives in UploadZone so this component stays a
// dumb presentation surface. Policy in one place, presentation here.
//
// stopPropagation on the wrapper because every current consumer is a
// dropzone where clicking the surface opens the file picker; clicking
// the checkbox or its label must not trigger that. In the disabled
// state we also intercept the click to route to /pro before propagation
// would have opened the picker.
//
// State is read from UploadContext (`keepOriginalNext` / `setKeepOriginalNext`)
// so every mount of this component sees the same per-session value — flipping
// it in one surface (e.g. /upload) and then opening a Collection-page upload
// shows the same checked state without prop-drilling.
//
// Two density variants: `normal` is the large dropzone treatment (mt-4 px-4
// py-3); `compact` is the collapsed "add more files" bar (mt-3 px-3 py-2.5)
// where vertical space is tighter.
export interface KeepOriginalToggleProps {
  density?: 'normal' | 'compact';
  /** When true, greys the row and turns it into an upgrade CTA to /pro. */
  disabled?: boolean;
}

const KeepOriginalToggle: React.FC<KeepOriginalToggleProps> = ({
  density = 'normal',
  disabled = false,
}) => {
  const { keepOriginalNext, setKeepOriginalNext } = useUpload();
  const navigate = useNavigate();

  const paddingClass = density === 'compact' ? 'mt-3 px-3 py-2.5' : 'mt-4 px-4 py-3';
  const stateClass = disabled ? 'opacity-70 hover:opacity-90 transition-opacity cursor-pointer' : '';

  return (
    <div
      className={`${paddingClass} rounded-lg border max-w-lg mx-auto text-left ${stateClass}`}
      style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'var(--border-color)' }}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) navigate('/pro');
      }}
    >
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={disabled ? false : keepOriginalNext}
          onChange={(e) => { if (!disabled) setKeepOriginalNext(e.target.checked); }}
          disabled={disabled}
          className={`mt-0.5 h-4 w-4 rounded border-white/30 bg-transparent text-soundwave-500 focus:ring-soundwave-500 focus:ring-offset-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          aria-describedby="keep-original-help"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Keep original file
            </span>
            {disabled && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-soundwave-500/15 text-soundwave-500">
                Pro
              </span>
            )}
          </span>
          <span id="keep-original-help" className="block text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Save your uploaded source file (WAV / FLAC / AIF) in addition to the MP3 we play. Counts against your storage.
          </span>
        </span>
      </label>
    </div>
  );
};

export default KeepOriginalToggle;
